import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from '@environments/environment';
import { failStatus, GenericRequest, successStatus } from '@core/models/request-status.model';
import { GetAvailableB2BUnitsResponse, PaymentMethod, B2BUnit, B2BUnitAddress } from '@b2b/features/b2b-unit/core/b2b-unit.model';

@Injectable({
  providedIn: 'root',
})
export class B2bUnitConnector {
  constructor(private http: HttpClient) {}

  getAvailableB2bUnits(searchTerms: { [key: string]: string | number }): Observable<GetAvailableB2BUnitsResponse> {
    const url = `${environment.apiService}/customer/units`;

    const filteredTerms = Object.entries(searchTerms).reduce(
      (acc, [key, value]) => {
        if (value !== '' && key !== 'type') {
          acc[key] = value;
        }
        return acc;
      },
      {} as { [key: string]: string | number },
    );

    return this.http.get<GetAvailableB2BUnitsResponse>(url, {
      params: { ...filteredTerms, limit: 5 },
    });
  }

  /**
   * Get the default B2BUnit for the current B2BCustomer
   * Calls: GET /customer/default-unit
   */
  getDefaultB2BUnit(): Observable<B2BUnit | null> {
    const url = `${environment.apiService}/customer/default-unit`;
    return this.http.get<B2BUnit | null>(url).pipe(
      catchError(() => {
        return of(null);
      }),
    );
  }

  /**
   * Get B2B Unit Details for the current selected unit (stored in state)
   * Calls: GET /customer/:b2bUnitId
   */
  getB2bUnitDetails(b2bUnitId: string): Observable<GenericRequest<B2BUnit>> {
    const url = `${environment.apiService}/customer/${b2bUnitId}`;

    return this.http.get<B2BUnit>(url).pipe(
      map(response => ({
        ...successStatus,
        data: {
          id: response.id,
          uid: response.uid,
          name: response.name,
        },
      })),
      catchError(error => {
        return of({
          ...failStatus,
          message: error?.message || '',
        });
      }),
    );
  }

  /**
   * Get B2B Unit Address for the current selected unit
   * Calls: GET /customer/:b2bUnitId
   */
  getB2BUnitAddress(b2bUnitId: string): Observable<GenericRequest<B2BUnitAddress | null>> {
    const url = `${environment.apiService}/customer/address/${b2bUnitId}`;

    return this.http.get<B2BUnitAddress>(url).pipe(
      map(response => ({
        ...successStatus,
        data: response,
      })),
      catchError(error => {
        return of({
          ...failStatus,
          data: null,
          message: error?.message || '',
        });
      }),
    );
  }

  getAvailablePaymentMethods(): Observable<GenericRequest<PaymentMethod[]>> {
    const url = `${environment.apiService}/payment-method`;
    return this.http.get<string[]>(url).pipe(
      map(response => ({
        ...successStatus,
        data: response.map(paymentMethod => ({
          id: paymentMethod,
          name: paymentMethod,
          code: paymentMethod,
        })),
      })),
      catchError(error => {
        return of({
          ...failStatus,
          message: error?.message || '',
        });
      }),
    );
  }

  getDefaultPaymentMethod(customerId: string, b2bUnitId: string): Observable<GenericRequest<PaymentMethod | null>> {
    const url = `${environment.apiService}/payment-method/user/${customerId}/client/${b2bUnitId}`;

    return this.http.get<string[]>(url).pipe(
      map(response => {
        const paymentMethods = response.map(paymentMethod => ({
          id: paymentMethod,
          name: paymentMethod,
          code: paymentMethod,
        }));

        return {
          ...successStatus,
          data: paymentMethods?.[0] || null,
        };
      }),
      catchError(error => {
        return of({
          ...failStatus,
          message: error?.message || '',
        });
      }),
    );
  }
}
